import { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import {
  Modal,
  Option,
  Select,
  useMessage,
  useProjectDetails,
  useUtils,
} from '@pepper/ui'

import SelectedTopicsHeader from '../SelectedTopicsHeader'

import {
  pluralize,
  pluralizeWithoutCount,
} from '@pepper/utils/src/function/pluralize'
import { createProject } from '@pepper/utils/src/api/projects/projects'
import { useSelectedCompanyId } from 'utils/functions/misc'
import { createPrivateDocument } from 'utils/api/item'
import { generateDraftData } from 'components/CreateItemModal/CreateItemModal.utils'

import type { Topic } from '@pepper/utils/src/types/seo'

import s from './CreateItemModal.module.css'

const _getWordCount = (topic: Topic) => {
  if (topic?.crr?.document) {
    const { min, max } = topic.crr.document.page_length
    return Math.floor((min + max) / 2 / 100) * 100
  } else {
    return undefined
  }
}

type Props = {
  open: boolean
  itemType: 'assignment' | 'private_doc'
  selectedTopics: Topic[]
  onClose: () => void
  onSuccess: () => void
}

export const CreateItemModal: React.FC<Props> = ({
  open,
  itemType,
  selectedTopics,
  onClose,
  onSuccess,
}) => {
  const isMarketplace = itemType === 'assignment'
  const companyId = useSelectedCompanyId()
  const history = useHistory()
  const { projectId } = useParams<{ projectId: string }>()
  const { verticals } = useUtils()
  const { showError, showSuccess } = useMessage()
  const [selectedVerticalId, setSelectedVerticalId] = useState<number>()
  const [isLoading, setIsLoading] = useState(false)

  const { data: projectDetails } = useProjectDetails(projectId)

  const handleClose = () => {
    setSelectedVerticalId(undefined)
    onClose()
  }

  const createPrivateDocs = async () => {
    try {
      const results = await Promise.all(
        selectedTopics.map(topic =>
          createPrivateDocument({
            title: topic.name,
            projectId,
            topic_id: topic.id,
          })
        )
      )
      const successCount = results.filter(
        res => res.status === 'ok' && res.data?.item_id
      ).length
      if (successCount === selectedTopics.length) {
        showSuccess(
          `${pluralize(successCount, 'document')} created successfully`
        )
        if (selectedTopics.length > 1) {
          history.push({
            pathname: `/project/${projectId}/items`,
            search: '?item_types=private_doc',
          })
        } else {
          window.open(
            `/${companyId}/items/${results[0].data?.item_id}`,
            '_blank'
          )
        }
      } else if (results.some(({ status }) => status !== 'ok')) {
        showError(
          `There was some glitch! Failed to create ${
            selectedTopics.length - successCount
          } of ${selectedTopics.length} documents`
        )
      }
    } catch (error) {
      showError('Unable to create documents. Please try again')
    }
  }

  const createAssignments = async () => {
    if (!selectedVerticalId) {
      showError('Please select a category')
      return
    }
    try {
      const {
        draftData,
        projectId: draftProjectId,
        error: draftError,
      } = await generateDraftData({
        companyId,
        items: selectedTopics.map(topic => {
          return {
            topic_id: topic.id,
            assignment_title: topic.name,
            word_count: _getWordCount(topic),
          }
        }),
        projects: projectDetails ? [projectDetails] : [],
        verticals,
        values: {
          projectId,
          verticalId: selectedVerticalId,
        },
      })
      if (draftError || !draftData || !draftProjectId) {
        showError(draftError ?? 'Unable to create Items')
        return
      }
      const {
        status,
        error,
        data: draftResponse,
      } = await createProject({
        body: draftData,
        type: 'draft',
      })
      if (status !== 'ok') {
        showError(error)
      }
      history.push(`/create-project-flow?draft_id=${draftResponse.id}`)
    } catch (error) {
      showError('Unable to create Items')
    }
  }

  const handleCreate = async () => {
    setIsLoading(true)
    if (isMarketplace && projectDetails) {
      await createAssignments()
    } else {
      await createPrivateDocs()
    }
    setIsLoading(false)
    handleClose()
    onSuccess()
  }

  return (
    <Modal
      title={isMarketplace ? 'Order from Expert Talent' : 'Create Docs'}
      visible={open}
      onCancel={handleClose}
      onOk={handleCreate}
      okText={`Create ${
        selectedTopics.length > 1 ? selectedTopics.length : ''
      } ${pluralizeWithoutCount(selectedTopics.length, 'Item')}`}
      okButtonProps={{
        loading: isLoading,
        disabled: isMarketplace ? !selectedVerticalId : false,
      }}
      destroyOnClose={true}
    >
      <div className={s.wrapper}>
        <SelectedTopicsHeader
          selectedTopics={selectedTopics.map(topic => ({
            id: topic.id,
            name: topic.name,
          }))}
        />
        {isMarketplace && (
          <div className={s.fieldWrapper}>
            <p className={s.bold}>
              Select a Category <span className={s.required}>*</span>
            </p>
            <Select
              placeholder="Select Category"
              onSelect={value => setSelectedVerticalId(Number(value))}
            >
              {verticals.map(vertical => (
                <Option key={vertical.id} value={vertical.id}>
                  {vertical.title}
                </Option>
              ))}
            </Select>
          </div>
        )}
      </div>
    </Modal>
  )
}
